import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../config/api';

const OnlineStatusContext = createContext();

export const useOnlineStatus = () => {
  const context = useContext(OnlineStatusContext);
  if (!context) throw new Error('useOnlineStatus must be used within an OnlineStatusProvider');
  return context;
};

export const OnlineStatusProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [lastSeen, setLastSeen] = useState(new Map());

  // Normalize IDs so that Map/Set lookups are consistent even if backend returns numbers
  // while UI passes strings (or vice versa).
  const normId = useCallback((id) => {
    if (id === null || typeof id === 'undefined') return null;
    const s = String(id).trim();
    return s ? s : null;
  }, []);

  // Für One-shot Fetch
  const hasFetchedOnline = useRef(false);
  const isMounted = useRef(true);
  const authFailedRef = useRef(false);
  useEffect(() => () => { isMounted.current = false; }, []);

  // Local cache keys / intervals
  const ONLINE_CACHE_KEY = 'online_users_cache_v1';
  const ONLINE_CACHE_TTL = 5 * 60 * 1000; // 5 Minuten

  // One-shot fetch der Online-User-Liste (mit lokalem Cache)
  const fetchOnlineUsers = useCallback(async (force = false) => {
    try {
      if (!force) {
        try {
          const raw = localStorage.getItem(ONLINE_CACHE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.ts && (Date.now() - parsed.ts) < ONLINE_CACHE_TTL) {
              if (!isMounted.current) return;
              const users = parsed.users || [];
              const onlineUserIds = new Set(users.map(u => normId(u.id)).filter(Boolean));
              const lastSeenMap = new Map(users.map(u => [normId(u.id), u.last_seen]).filter(([k]) => !!k));
              setOnlineUsers(onlineUserIds);
              setLastSeen(lastSeenMap);
              return;
            }
          }
        } catch (e) {
          // ignore cache read errors and fallthrough to network fetch
        }
      }

      const response = await api.get('/api/users/online');
      if (!isMounted.current) return;
      const users = response.data.users || [];
      const onlineUserIds = new Set(users.map(u => normId(u.id)).filter(Boolean));
      const lastSeenMap = new Map(users.map(u => [normId(u.id), u.last_seen]).filter(([k]) => !!k));
      setOnlineUsers(onlineUserIds);
      setLastSeen(lastSeenMap);

      // write cache
      try {
        localStorage.setItem(ONLINE_CACHE_KEY, JSON.stringify({ ts: Date.now(), users }));
      } catch (e) {
        // ignore storage errors
      }
    } catch (err) {
      console.error('Failed to fetch online users:', err);
    }
  }, []);

  // Check if ein User online ist (aus Cache)
  const isUserOnline = useCallback((userId) => {
    const id = normId(userId);
    if (!id) return false;
    const ts = lastSeen.get(id);
    if (!ts) return false;
    const fiveMin = new Date(Date.now() - 5 * 60 * 1000);
    return new Date(ts) > fiveMin;
  }, [lastSeen, normId]);

  // Initiale Abfrage nur einmal pro Session, kein ständiges Polling
  useEffect(() => {
    if (isAuthenticated && !hasFetchedOnline.current) {
      fetchOnlineUsers();
      hasFetchedOnline.current = true;
    }
  }, [isAuthenticated, fetchOnlineUsers]);

  // Keep the online list reasonably fresh on pages where avatars are visible.
  // This should remain lightweight to avoid triggering server rate limits.
  useEffect(() => {
    if (!isAuthenticated) return;
    const getIntervalMs = () => {
      try {
        // slower when tab is hidden
        return (typeof document !== 'undefined' && document.hidden) ? 5 * 60 * 1000 : 2 * 60 * 1000;
      } catch (_) {
        return 2 * 60 * 1000;
      }
    };

    let id;
    const start = () => {
      if (id) clearInterval(id);
      id = setInterval(() => {
        fetchOnlineUsers(true);
      }, getIntervalMs());
    };

    start();
    const onVis = () => start();
    try { document.addEventListener('visibilitychange', onVis); } catch (_) { }

    return () => clearInterval(id);
  }, [isAuthenticated, fetchOnlineUsers]);

  // Send "online" status on mount and periodically while the user is authenticated.
  const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 Minuten

  useEffect(() => {
    let intervalId;

    const sendOnlineStatus = async () => {
      if (!isAuthenticated || !user) return;
      if (authFailedRef.current) return;
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        // best-effort notify backend that this user is online
        await api.post('/api/users/online-status', {
          status: 'online',
          last_seen: new Date().toISOString()
        });
      } catch (err) {
        if (err?.response?.status === 401) {
          authFailedRef.current = true;
          return;
        }
        // Non-fatal — keep trying on interval
        console.error('Failed to update online status:', err);
      }
    };

    const startHeartbeat = () => {
      // Immediately notify server we're online
      sendOnlineStatus();
      // choose interval; if tab hidden, increase interval to save resources
      const effectiveInterval = (typeof document !== 'undefined' && document.hidden) ? HEARTBEAT_INTERVAL * 2 : HEARTBEAT_INTERVAL;
      intervalId = setInterval(sendOnlineStatus, effectiveInterval);
    };

    if (isAuthenticated && user) {
      startHeartbeat();

      const onVisibilityChange = () => {
        if (intervalId) clearInterval(intervalId);
        startHeartbeat();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        if (intervalId) clearInterval(intervalId);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAuthenticated, user]);

  // Optional: Vor dem Verlassen der Seite offline melden
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAuthenticated && user) {
        const token = (() => {
          try { return localStorage.getItem('token'); } catch (_) { return null; }
        })();

        // Prefer fetch keepalive because it CAN send Authorization headers.
        // sendBeacon can't send custom headers, which causes frequent 401s.
        try {
          if (token && typeof fetch === 'function') {
            fetch('/api/users/online-status', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({ status: 'offline', last_seen: new Date().toISOString() }),
              keepalive: true
            });
            return;
          }
        } catch (e) {
          // fall through to sendBeacon
        }

        // Fallback (no auth header possible). Server treats it as best-effort no-op.
        if (navigator.sendBeacon) {
          try {
            const payload = JSON.stringify({ status: 'offline', last_seen: new Date().toISOString() });
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon('/api/users/online-status', blob);
          } catch (e) {
            // ignore sendBeacon errors
          }
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAuthenticated, user]);

  const value = {
    onlineUsers,
    lastSeen,
    isUserOnline,
    fetchOnlineUsers
  };

  return (
    <OnlineStatusContext.Provider value={value}>
      {children}
    </OnlineStatusContext.Provider>
  );
};
